// crewbit-admin/src/hooks/useResolveScheduleDefaultView.ts
//
// Schedule Redesign v1 canonical resolver hook.
// Returns { zoom, lens } from resolve_schedule_view.

import { useCallback } from 'react';
import {
  callScheduleQueryRpc,
  type ScheduleQueryRpcResult,
} from '../lib/scheduleRpc';
import { useScheduleViewStore } from '../stores/scheduleViewStore';

export type ScheduleZoom = 'today' | 'day' | 'week' | 'month' | 'year';
export type ScheduleLens = 'calendar' | 'crew' | 'route';
export type ScheduleSurface = 'mobile' | 'desktop';

export interface ResolveScheduleViewInput {
  userId: string;
  surface: ScheduleSurface;
}

export interface ResolveScheduleViewValue {
  zoom: ScheduleZoom;
  lens: ScheduleLens;
}

export type ResolveScheduleViewResult = ScheduleQueryRpcResult<ResolveScheduleViewValue>;

const CACHE_STALE_MS = 24 * 60 * 60 * 1000;
const inFlightKeys = new Set<string>();

async function fetchAndCache(input: ResolveScheduleViewInput): Promise<ResolveScheduleViewResult> {
  const result = await callScheduleQueryRpc<ResolveScheduleViewValue>('resolve_schedule_view', {
    p_user_id: input.userId,
    p_surface: input.surface,
  });
  if (result.success) {
    useScheduleViewStore.getState().setCache({
      zoom: result.data.zoom,
      lens: result.data.lens,
    });
  }
  return result;
}

function revalidateInBackground(input: ResolveScheduleViewInput): void {
  const key = `${input.userId}:${input.surface}`;
  if (inFlightKeys.has(key)) return;
  inFlightKeys.add(key);
  void fetchAndCache(input).finally(() => {
    inFlightKeys.delete(key);
  });
}

export function useResolveScheduleView() {
  return useCallback(
    async (
      input: ResolveScheduleViewInput,
    ): Promise<ResolveScheduleViewResult> => {
      const { resolved, lastFetched } = useScheduleViewStore.getState();
      if (resolved) {
        const isStale = !lastFetched || Date.now() - lastFetched > CACHE_STALE_MS;
        if (isStale) {
          revalidateInBackground(input);
        } else {
          // Keep cache eventually consistent with server defaults/preferences.
          revalidateInBackground(input);
        }
        return { success: true, data: { zoom: resolved.zoom, lens: resolved.lens } };
      }
      return fetchAndCache(input);
    },
    [],
  );
}
